import React, { useState, useEffect } from 'react';
import { projectsAPI, designOptionsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { GitCompare, ArrowRight, TrendingDown, TrendingUp, Minus, Trophy, Leaf } from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
  Cell,
} from 'recharts';

const CompareDesigns = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);

  const COLORS = ['#0F2818', '#800020', '#3B82F6', '#8B5CF6'];

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      const projectsWithOptions = response.data.filter(
        (p) => p.design_options && p.design_options.length >= 2
      );
      setProjects(projectsWithOptions);
      if (projectsWithOptions.length > 0) {
        setSelectedProject(projectsWithOptions[0]);
      }
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (projectId) => {
    const project = projects.find((p) => p.id === projectId);
    setSelectedProject(project);
    setSelectedOptions([]);
    setComparison(null);
  };

  const handleOptionToggle = (optionId) => {
    if (selectedOptions.includes(optionId)) {
      setSelectedOptions(selectedOptions.filter((id) => id !== optionId));
    } else if (selectedOptions.length < 4) {
      setSelectedOptions([...selectedOptions, optionId]);
    } else {
      toast.error('Maximum 4 options can be compared');
    }
  };

  const handleCompare = async () => {
    if (selectedOptions.length < 2) {
      toast.error('Please select at least 2 design options');
      return;
    }

    setComparing(true);
    try {
      const response = await designOptionsAPI.compare(selectedProject.id, selectedOptions);
      setComparison(response.data);
      toast.success('Comparison generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Comparison failed');
    } finally {
      setComparing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-4 border-[#0F2818] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Chart data
  const carbonComparisonData = comparison?.options.map((opt) => ({
    name: opt.name.length > 15 ? opt.name.substring(0, 15) + '...' : opt.name,
    carbon: opt.total_carbon,
  })) || [];

  // Radar chart data - comparing categories across options
  const allCategories = new Set();
  comparison?.options.forEach((opt) => {
    Object.keys(opt.carbon_by_category || {}).forEach((cat) => allCategories.add(cat));
  });

  const radarData = Array.from(allCategories).map((category) => {
    const entry = { category };
    comparison?.options.forEach((opt, index) => {
      entry[`option${index + 1}`] = opt.carbon_by_category?.[category] || 0;
    });
    return entry;
  });

  return (
    <div className="p-8" data-testid="compare-designs-page">
      {/* Header */}
      <div className="page-header -mx-8 -mt-8 mb-8 px-8 py-6 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Compare Designs
            </h1>
            <p className="text-slate-600 mt-1">
              Side-by-side comparison of design options
            </p>
          </div>
          <Button
            className="bg-[#0F2818] hover:bg-[#06140C]"
            onClick={handleCompare}
            disabled={comparing || selectedOptions.length < 2}
            data-testid="compare-button"
          >
            {comparing ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Comparing...
              </span>
            ) : (
              <>
                <GitCompare className="w-4 h-4 mr-2" />
                Compare Selected
              </>
            )}
          </Button>
        </div>
      </div>

      {projects.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <GitCompare className="w-16 h-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              No Projects Available
            </h3>
            <p className="text-slate-600 text-center max-w-sm">
              Create a project with at least 2 design options to start comparing
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {/* Project & Option Selection */}
          <Card className="border-slate-200" data-testid="selection-card">
            <CardHeader className="border-b border-slate-100 pb-4">
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Select Options to Compare
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Project</label>
                  <Select
                    value={selectedProject?.id}
                    onValueChange={handleProjectChange}
                  >
                    <SelectTrigger className="w-full max-w-md" data-testid="project-select">
                      <SelectValue placeholder="Select a project" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name} ({project.design_options?.length || 0} options)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedProject && (
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-slate-700">
                      Design Options (Select 2-4)
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {selectedProject.design_options?.map((option, index) => (
                        <div
                          key={option.id}
                          onClick={() => handleOptionToggle(option.id)}
                          className={`p-4 border rounded-lg cursor-pointer transition-all ${
                            selectedOptions.includes(option.id)
                              ? 'border-[#0F2818] bg-[#E8EEEA]'
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          data-testid={`option-${option.id}`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                              style={{
                                backgroundColor: selectedOptions.includes(option.id)
                                  ? COLORS[selectedOptions.indexOf(option.id)]
                                  : '#E2E8F0',
                                color: selectedOptions.includes(option.id) ? '#fff' : '#64748B',
                              }}
                            >
                              {index + 1}
                            </div>
                            {selectedOptions.includes(option.id) && (
                              <Badge className="bg-[#0F2818] text-white">Selected</Badge>
                            )}
                          </div>
                          <h4 className="font-semibold text-slate-900 mb-1">{option.name}</h4>
                          <p className="text-sm font-mono text-[#0F2818]">
                            {(option.total_carbon / 1000).toFixed(2)} tCO2e
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Comparison Results */}
          {comparison && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="border-slate-200 border-l-4 border-l-green-500" data-testid="best-option-card">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <Trophy className="w-5 h-5 text-green-500" />
                      <span className="text-sm text-slate-500 uppercase tracking-wider">Best Option</span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-1">
                      {comparison.comparison_summary.best_option.name}
                    </h3>
                    <p className="font-mono text-green-600">
                      {(comparison.comparison_summary.best_option.carbon / 1000).toFixed(2)} tCO2e
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 border-l-4 border-l-[#800020]" data-testid="savings-card">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <TrendingDown className="w-5 h-5 text-[#800020]" />
                      <span className="text-sm text-slate-500 uppercase tracking-wider">Potential Savings</span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-1">
                      {(comparison.comparison_summary.carbon_savings / 1000).toFixed(2)} tCO2e
                    </h3>
                    <p className="text-[#800020] font-medium">
                      {comparison.comparison_summary.percentage_reduction}% reduction
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 border-l-4 border-l-slate-400" data-testid="average-card">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <Minus className="w-5 h-5 text-slate-500" />
                      <span className="text-sm text-slate-500 uppercase tracking-wider">Average Carbon</span>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-1">
                      {(comparison.comparison_summary.average_carbon / 1000).toFixed(2)} tCO2e
                    </h3>
                    <p className="text-slate-500">
                      Across {comparison.options.length} options
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Bar Chart */}
                <Card className="border-slate-200" data-testid="bar-chart">
                  <CardHeader className="border-b border-slate-100 pb-4">
                    <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Total Carbon Comparison
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={carbonComparisonData}>
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(1)}t`} />
                        <Tooltip
                          formatter={(value) => [`${(value / 1000).toFixed(2)} tCO2e`, 'Carbon']}
                          contentStyle={{ borderRadius: 6, border: '1px solid #E2E8F0' }}
                        />
                        <Bar dataKey="carbon" radius={[4, 4, 0, 0]}>
                          {carbonComparisonData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Radar Chart */}
                <Card className="border-slate-200" data-testid="radar-chart">
                  <CardHeader className="border-b border-slate-100 pb-4">
                    <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      Category Breakdown
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    {radarData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <RadarChart data={radarData}>
                          <PolarGrid />
                          <PolarAngleAxis dataKey="category" tick={{ fontSize: 10 }} />
                          {comparison.options.map((opt, index) => (
                            <Radar
                              key={opt.id}
                              name={opt.name}
                              dataKey={`option${index + 1}`}
                              stroke={COLORS[index]}
                              fill={COLORS[index]}
                              fillOpacity={0.2}
                            />
                          ))}
                          <Legend />
                          <Tooltip
                            formatter={(value) => [`${(value / 1000).toFixed(2)} tCO2e`, 'Carbon']}
                            contentStyle={{ borderRadius: 6, border: '1px solid #E2E8F0' }}
                          />
                        </RadarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-64 text-slate-500">
                        No category data available
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Detailed Table */}
              <Card className="border-slate-200" data-testid="comparison-table">
                <CardHeader className="border-b border-slate-100 pb-4">
                  <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Detailed Comparison
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Design Option</TableHead>
                        <TableHead className="text-right">Materials</TableHead>
                        <TableHead className="text-right">Total Carbon</TableHead>
                        <TableHead className="text-right">vs Best</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {comparison.options
                        .sort((a, b) => a.total_carbon - b.total_carbon)
                        .map((opt, index) => {
                          const isBest = opt.id === comparison.comparison_summary.best_option.id;
                          const diffFromBest = opt.total_carbon - comparison.comparison_summary.best_option.carbon;
                          
                          return (
                            <TableRow key={opt.id}>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{opt.name}</span>
                                  {isBest && (
                                    <Badge className="bg-green-100 text-green-700">Best</Badge>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-mono">
                                {opt.materials?.length || 0}
                              </TableCell>
                              <TableCell className="text-right font-mono font-medium text-[#0F2818]">
                                {(opt.total_carbon / 1000).toFixed(2)} tCO2e
                              </TableCell>
                              <TableCell className="text-right">
                                {isBest ? (
                                  <span className="text-green-600 flex items-center justify-end gap-1">
                                    <Trophy className="w-4 h-4" />
                                    Lowest
                                  </span>
                                ) : (
                                  <span className="text-red-500 flex items-center justify-end gap-1">
                                    <TrendingUp className="w-4 h-4" />
                                    +{(diffFromBest / 1000).toFixed(2)}t
                                  </span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CompareDesigns;
